import React, { useEffect, useState } from "react";

import classnames from "classnames";
import { basicInformationURL } from "EmployeeApplication/urls";
import EditEmployeeModal from "EmployerApplication/Components/EditEmployeeModal";
import PartnerEditEmployeeModal from "EmployerApplication/Components/PartnerEditEmployeeModal";
import { Link } from "react-router-dom";
import { reduxForm } from "redux-form";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { canEdit } from "Utils/canEdit";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";

import { MenuDropdown, Option } from "Cobalt/Dropdown";
import { StandaloneTrigger } from "Cobalt/Modal";
import { Table, TD, TR, TH } from "Cobalt/Table";
import { Span } from "Common/HTML";
import Icon from "Common/Icon";
import PartnerContext, { usePartnerContext } from "Common/PartnerContext";
import RemoveIcon from "Common/RemoveIcon";
import { SizeSwitch } from "Common/Responsive";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import makeBem from "Utils/makeBem";

const bem = makeBem("EmployeeTable");

const completeApplicationLink = (employee) => {
  return (
    <Link to={basicInformationURL(true, false, employee.hash_id)}>
      <Icon icon="pencil-alt" mr={3} />
      Application
    </Link>
  );
};

const EditEmployeeTriggerAndModal = ({ employee }) => (
  <StandaloneTrigger
    employee={employee}
    trigger={(props) => (
      <Option
        onClick={(e) => {
          AnalyticsTracking.trackEvent("cta_clicked", {
            location: "review_employee_application_status_page",
            label: "edit",
          });

          props.onClick(e);
        }}
        style={{ cursor: "pointer" }}
      >
        <Icon icon="pencil-alt" flavor="solid" mr={3} />
        Edit
      </Option>
    )}
    modal={(modalProps) => (
      <PartnerContext.Consumer>
        {(props) => (
          <div>
            {props.partnerContextEnabled ? (
              <PartnerEditEmployeeModal
                form={`editEmployeeForm_${employee.hash_id}`}
                include1099={props.siManaged1099s}
                includeOwner={props.siManagedOwners}
                {...modalProps}
              />
            ) : (
              <EditEmployeeModal form={`editEmployeeForm_${employee.hash_id}`} {...modalProps} />
            )}
          </div>
        )}
      </PartnerContext.Consumer>
    )}
  />
);

const EditButtons = ({ employee, disableRemove, handleClickDeleteEmployee }) => (
  <>
    <EditEmployeeTriggerAndModal employee={employee} />
    <Option className={bem(null, "red")} onClick={() => handleClickDeleteEmployee()}>
      {disableRemove[employee.hash_id] ? (
        <>
          <Icon icon="cog" mr={3} spin flavor="solid" />
          Loading...
        </>
      ) : (
        <>
          Remove
          <RemoveIcon />
        </>
      )}
    </Option>
  </>
);

const MenuCellContents = ({ employee, disableRemove, emailReminderLink, emailReminderHandlerForEmployee, handleClickDeleteEmployee, disableEdits }) => (
  <SizeSwitch
    md={
      canEdit(employee) ? (
        <MenuDropdown pullRight>
          <EditButtons employee={employee} disableRemove={disableRemove || disableEdits} handleClickDeleteEmployee={handleClickDeleteEmployee} />
        </MenuDropdown>
      ) : (
        <></>
      )
    }
    // One thing here to have attention, the Option always should handle the action and not its children
    // so we need the "emailReminderHandlerForEmployee"
    xs={
      <MenuDropdown usePortal pullRight>
        <Option to={basicInformationURL(true, false, employee.hash_id)} className={bem(null, "menuLink")}>
          <Icon icon="check" mr={3} />
          Application
        </Option>
        {employee.email && (
          <Option className={bem(null, "menuLink")} onClick={emailReminderHandlerForEmployee}>
            {emailReminderLink}
          </Option>
        )}

        {canEdit(employee) && !disableEdits && (
          <EditButtons employee={employee} disableRemove={disableRemove} handleClickDeleteEmployee={handleClickDeleteEmployee} />
        )}
      </MenuDropdown>
    }
  />
);

const mapApplicationStatus = (employee) => {
  if (employee.status === "Done") {
    if (employee.is_enrolling === "Yes" || employee.is_enrolling_dental === "Yes" || employee.is_enrolling_vision === "Yes") {
      return "Enrolling";
    }
    return "Declining";
  }
  return employee.status;
};

const splitName = (name) => {
  const names = name.split(" ");
  return {
    firstName: names[0],
    lastName: names.slice(1).join(" "),
  };
};

const statusRank = (status) => {
  if (status === "Done") {
    return 3;
  }
  if (status === "In Progress") {
    return 2;
  }
  return 1;
};

function compareEmployees(a, b) {
  if (statusRank(a.status) === statusRank(b.status)) {
    const aName = splitName(a.full_name);
    const bName = splitName(b.full_name);

    if (aName.lastName === bName.lastName) {
      return aName.firstName.localeCompare(bName.firstName);
    }
    return aName.lastName.localeCompare(bName.lastName);
  }
  return statusRank(a.status) > statusRank(b.status) ? 1 : -1;
}

export const ReviewAndEditEmployeeTableDumb = (props) => {
  const [disableRemove, setDisableRemove] = useState({});
  const [remindersSent, setRemindersSent] = useState({});
  const [emailStatuses, setEmailStatuses] = useState({});

  useEffect(() => {
    const url = appendAuthParams("/api/v2/company-applications/customer_io_application_email_status");

    fetch(url)
      .then((response) => response.json())
      .then((result) => {
        setEmailStatuses(result);
      });
  }, []);

  const handleClickDeleteEmployee = (hashId) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "review_employee_application_status_page",
      label: "delete",
    });

    props.onDeleteEmployee(hashId);
    setDisableRemove({ ...disableRemove, [hashId]: true });
  };

  const handleSendReminder = (hashId) => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "review_employee_application_status_page",
      label: "send_reminder",
    });

    setRemindersSent({ ...remindersSent, [hashId]: true });
    const employeeIndex = props.employees.findIndex((x) => x.hash_id === hashId);

    const url = props.employees[employeeIndex].send_employee_application_link;
    const token = companyToken();
    const queryString = token ? `&company_id_token=${token}` : "";
    return fetchWithCSRFToken(`${url}${queryString}`, {
      method: "POST",
      processData: false,
    });
  };

  const { disableEdits } = props;
  let { partnerName } = props;
  partnerName = partnerName || usePartnerContext().partnerName;

  const emailReminderHandler = (employee) => {
    handleSendReminder(employee.hash_id);
  };
  const emailReminderLink = (employee) => {
    const emailAlreadySent = !!emailStatuses[employee.hash_id];
    const emailJustSent = !!remindersSent[employee.hash_id];

    return (
      <Span id={`sendEmail_${employee.hash_id}`} className={bem(null, "tableLink")} onClick={() => emailReminderHandler(employee)}>
        <Icon icon="envelope" mr={3} />
        {emailAlreadySent || emailJustSent ? "Resend" : "Send"}
      </Span>
    );
  };

  return (
    <Table aria-label="Employee Table">
      <thead>
        <TR>
          <TH>Employee</TH>
          <TH>Status</TH>
          <TH className="max-md:hidden" />
          <TH className={classnames(bem("Reminder"), "max-md:hidden")}>Reminder</TH>
          <TH className={disableEdits || (partnerName && partnerName !== "none") ? "md:hidden" : ""}>Actions</TH>
        </TR>
      </thead>
      <tbody>
        {props.employees.sort(compareEmployees).map(
          (employee) =>
            employee.is_eligible && (
              <TR key={employee.hash_id} data-testid={`hash_id:${employee.hash_id}`}>
                <TD className={bem("Name")}>{employee.full_name}</TD>
                <TD>{mapApplicationStatus(employee)}</TD>
                <TD className="max-md:hidden">{completeApplicationLink(employee)}</TD>
                <TD className="max-md:hidden">{employee.email && emailReminderLink(employee)}</TD>
                <TD className={classnames(bem("MenuCell"), disableEdits || (partnerName && partnerName !== "none") ? "md:hidden" : "")}>
                  <MenuCellContents
                    employee={employee}
                    disableRemove={disableRemove}
                    emailReminderLink={emailReminderLink(employee)}
                    emailReminderHandlerForEmployee={() => emailReminderHandler(employee)}
                    handleClickDeleteEmployee={() => handleClickDeleteEmployee(employee.hash_id)}
                    disableEdits={disableEdits}
                  />
                </TD>
              </TR>
            ),
        )}
      </tbody>
    </Table>
  );
};

const withReduxForm = reduxForm({
  form: "addEmployeeForm",
});

export default withReduxForm(ReviewAndEditEmployeeTableDumb);
